import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { selectLocalUser } from 'features/users/usersSlice';
import UserAvatar from 'components/UserAvatar';
import { useAvatarUpload } from './useAvatarUpload';

type AvatarUIState =
  'idle-no-avatar' | 'previewing' | 'idle-has-avatar' | 'editing' | 'previewing-replace';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const isCompactState = (state: AvatarUIState) =>
  state === 'idle-no-avatar' || state === 'idle-has-avatar';

export const AvatarUploadSection = () => {
  const { t } = useTranslation('settings');

  const localUser = useAppSelector(selectLocalUser);
  const { uploading, error: uploadError, upload, deleteAvatar, clearError } = useAvatarUpload();

  const avatarUrl = localUser?.avatarUrl;
  const userName = localUser?.name || '';
  const avatarColor = localUser?.avatarColor || '';
  const initials = localUser?.initials;

  const [uiState, setUIState] = useState<AvatarUIState>(
    avatarUrl ? 'idle-has-avatar' : 'idle-no-avatar'
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync idle state when avatarUrl changes (e.g., after avatarChanged/avatarDeleted event)
  useEffect(() => {
    if (isCompactState(uiState)) {
      setUIState(avatarUrl ? 'idle-has-avatar' : 'idle-no-avatar');
    }
  }, [avatarUrl]); // eslint-disable-line react-hooks/exhaustive-deps -- only react to avatarUrl changes, not uiState

  // Revoke previous blob URL when previewUrl changes or on unmount
  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (event.target) {
        event.target.value = '';
      }

      if (!file) {
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setLocalError(t('workspace.file_too_large'));
        return;
      }

      setLocalError(null);

      const newPreviewUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewUrl(newPreviewUrl);

      if (uiState === 'idle-no-avatar' || uiState === 'previewing') {
        setUIState('previewing');
      } else {
        setUIState('previewing-replace');
      }
    },
    [uiState, t]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    const success = await upload(selectedFile);

    if (success) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setUIState('idle-has-avatar');
    }
  }, [upload, selectedFile]);

  const handleCancel = useCallback(() => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setLocalError(null);
    clearError();

    if (uiState === 'previewing') {
      setUIState('idle-no-avatar');
    } else if (uiState === 'previewing-replace' || uiState === 'editing') {
      setUIState('idle-has-avatar');
    }
  }, [uiState, clearError]);

  const handleDelete = useCallback(() => {
    deleteAvatar();
    setUIState('idle-no-avatar');
  }, [deleteAvatar]);

  if (!localUser) {
    return null;
  }

  const displayError = localError || uploadError;

  const fileInput = (
    <input
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
      ref={fileInputRef}
      style={{ display: 'none' }}
      onChange={handleFileSelect}
    />
  );

  if (isCompactState(uiState)) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center', minWidth: 0 }}>
            <UserAvatar
              src={avatarUrl}
              name={userName}
              initials={initials}
              color={avatarColor}
              sx={{
                width: 80,
                height: 80,
                fontSize: 32,
                flexShrink: 0,
              }}
            />
            <Typography noWrap>{userName}</Typography>
          </Box>
          {uiState === 'idle-no-avatar' ? (
            <Button
              variant="contained"
              onClick={handleUploadClick}
              sx={{
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              {t('workspace.btn_upload_photo_label')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => setUIState('editing')}
              sx={{
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              {t('workspace.btn_edit_photo_label')}
            </Button>
          )}
        </Box>
        {displayError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {displayError}
          </Typography>
        )}
        {fileInput}
      </Box>
    );
  }

  // Preview / editing states
  const previewSrc = previewUrl || avatarUrl;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={previewSrc || undefined}
          alt={userName}
          sx={{
            width: 240,
            height: 240,
          }}
        />
        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: 'rgba(0, 0, 0, 0.4)',
            }}
          >
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )}
      </Box>

      {displayError && (
        <Typography color="error" variant="body2" sx={{ mt: -1 }}>
          {displayError}
        </Typography>
      )}

      <Stack direction="row" spacing={3} sx={{ width: '100%' }}>
        {uiState === 'editing' ? (
          <>
            <Button
              onClick={handleCancel}
              disabled={uploading}
              variant="outlined"
              sx={{
                flex: 1,
                fontWeight: 400,
              }}
            >
              {t('workspace.btn_cancel_label')}
            </Button>
            <Button
              onClick={handleDelete}
              disabled={uploading}
              variant="outlined"
              sx={{
                flex: 1,
                fontWeight: 400,
              }}
            >
              {t('workspace.btn_delete_label')}
            </Button>
            <Button
              onClick={handleUploadClick}
              disabled={uploading}
              variant="contained"
              sx={{
                flex: 1,
                fontWeight: 400,
              }}
            >
              {t('workspace.btn_replace_photo_label')}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleCancel}
              disabled={uploading}
              variant="outlined"
              sx={{
                flex: 1,
                fontWeight: 400,
              }}
            >
              {t('workspace.btn_cancel_label')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={uploading || !selectedFile}
              variant="contained"
              sx={{
                flex: 1,
                fontWeight: 400,
              }}
            >
              {t('workspace.btn_save_label')}
            </Button>
          </>
        )}
      </Stack>

      {fileInput}
    </Box>
  );
};
